import { useEffect, useRef } from 'react'
import { Drawer, Spin, Typography, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { useStore } from '../Stores'
import './chat.css'
import ChatList from './ChatList/ChatList'
import ChatHeader from './ChatWindow/ChatHeader'
import MessageList from './ChatWindow/MessageList'
import ChatSearchPanel from './ChatWindow/ChatSearchPanel'
import ChatInfoPanel from './ChatWindow/ChatInfoPanel'
import MessageInput from './ChatWindow/MessageInput'

const SIDEBAR_WIDTH_TABLET = 240
const SIDEBAR_WIDTH_DESKTOP = 300
const DRAWER_WIDTH_DEFAULT = 900
const DRAWER_WIDTH_WITH_PANEL = 1220
/**
 * Базовий рівень чату (над ресурсними дроверами antd з дефолтом 1000);
 * актуальне значення живе в uiStore.chatDrawerZIndex — воно динамічне
 * («останній відкритий — зверху»). Попапи antd (dropdown 1050+, preview
 * 1080) завжди вище. Модалки чату беруть CHAT_DRAWER_Z_INDEX + 1.
 */
export const CHAT_DRAWER_Z_INDEX = 1001

const ChatDrawer = observer(() => {
  const { uiStore, userStore, chatStore } = useStore()
  const { token } = theme.useToken()
  const hasLoaded = useRef(false)

  const { isMobile, isTablet, isCompact } = uiStore
  const hasActiveChat = !!uiStore.activeChatId
  const hasSidePanel = uiStore.isMessageSearchOpen || uiStore.isChatInfoOpen

  const sidebarWidth = isMobile ? 0 : isTablet ? SIDEBAR_WIDTH_TABLET : SIDEBAR_WIDTH_DESKTOP
  const drawerWidth = isCompact
    ? '100%'
    : hasSidePanel ? DRAWER_WIDTH_WITH_PANEL : DRAWER_WIDTH_DEFAULT

  // On mobile: show either chat list or chat window, not both
  const showChatList = !isMobile || !hasActiveChat
  const showChatWindow = !isMobile || hasActiveChat

  // Читання реєструє сам факт відкритого чату, а не клік по рядку списку:
  // чат відкривають ще й з кнопки «Чат» на картці завдання, з тоста і одразу
  // після створення — бейдж має гаснути однаково скрізь. Ефект також
  // самолікує випадок, коли перезавантаження списку повертає лічильник
  // із сервера в уже відкритий чат (суть ehs c158f30)
  const activeChatId = uiStore.activeChatId
  const activeChatUnreadCount = chatStore.activeChat?.unreadCount ?? 0

  useEffect(() => {
    if (!uiStore.isDrawerOpen || !activeChatId) return
    if (activeChatUnreadCount === 0) return

    chatStore.confirmChatRead(activeChatId)
  }, [uiStore.isDrawerOpen, activeChatId, activeChatUnreadCount, chatStore])

  // Load on mount (not on first open): the unread badge on ChatButton needs
  // the chat list — and message:new realtime events need chats to bump — even
  // before the drawer is ever opened
  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true
      userStore.loadUsers()
      chatStore.loadChats()
    }
  }, [userStore, chatStore])

  // Квазі-пол контактів прибрано: онлайн-статус тепер живе на
  // presence-каналі Centrifugo (useChatPresence) — realtime без запитів

  return (
    <Drawer
      title={null}
      placement="right"
      open={uiStore.isDrawerOpen}
      onClose={() => uiStore.closeDrawer()}
      width={drawerWidth}
      zIndex={uiStore.chatDrawerZIndex}
      closable={false}
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        },
      }}
    >
      {/* Клік будь-де в чаті повертає його поверх картки завдання
          (capture — щоб спрацювало до будь-яких stopPropagation) */}
      <div
        onMouseDownCapture={() => uiStore.bringChatToFront()}
        style={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}
      >
      {/* Кнопка «Чат» у завданні чи заявці: дровер відкрито одразу, get-or-create ще
          летить — оверлей дає миттєвий фідбек замість мертвої паузи.
          Тло — колір панелі, а не маска: сіра маска поверх списку чатів разом
          зі вкладеним Spin (власне розмите тло під `tip`) давала сірий
          прямокутник посеред дровера. Тож спінер і підпис — окремо. */}
      {chatStore.isOpeningEntityChat && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: token.marginSM,
            background: token.colorBgContainer,
          }}
        >
          <Spin size="large" />
          <Typography.Text type="secondary">
            {chatStore.openingEntityType === 'application' ? 'Відкриваю чат заявки…' : 'Відкриваю чат завдання…'}
          </Typography.Text>
        </div>
      )}

      {/* Left column — chat list */}
      {showChatList && (
        <div
          style={{
            width: isMobile ? '100%' : sidebarWidth,
            minWidth: isMobile ? '100%' : sidebarWidth,
            borderRight: isMobile ? 'none' : `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: token.colorBgContainer,
          }}
        >
          <ChatList />
        </div>
      )}

      {/* Center column — chat window. minWidth: 0 — інакше довга назва
          чату в шапці (одним рядком) розпирала колонку за край екрана */}
      {showChatWindow && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: token.colorBgLayout,
          }}
        >
          {!hasActiveChat ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: token.colorTextSecondary,
                fontSize: 16,
              }}
            >
              Оберіть чат, щоб почати листування
            </div>
          ) : (
            <>
              <ChatHeader />
              {/* On compact screens: search/info replaces messages */}
              {isCompact && uiStore.isMessageSearchOpen ? (
                <ChatSearchPanel />
              ) : isCompact && uiStore.isChatInfoOpen ? (
                <ChatInfoPanel />
              ) : (
                <>
                  <MessageList />
                  <MessageInput />
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Right column — search/info (desktop only) */}
      {!isCompact && uiStore.isMessageSearchOpen && <ChatSearchPanel />}
      {!isCompact && uiStore.isChatInfoOpen && <ChatInfoPanel />}
      </div>
    </Drawer>
  )
})

export default ChatDrawer
